import { IonTextarea } from '@ionic/react';
import styled from 'styled-components';
import { AllieButtonCorner } from '../../Buttons/Button/Button.types';
import { AllieInputType } from '../Input';
import { AllieTextAreaSize } from './TextArea.types';
import { ComponentType } from 'react';

const textColor = {
  active: 60,
  hover: 60,
  focus: 60,
  initial: 90,
  disabled: 50,
};

type InternalButtonState = 'initial' | 'active' | 'hover' | 'focus';

const backgroundColor = {
  primary: 10,
  secondary: 5,
  warning: 20,
  error: 20,
  neutral: 10,
};

const colorByType = {
  primary: 'neutral',
  secondary: 'neutral',
  warning: 'secondary',
  error: 'error',
};

const getBackground = (allieType: AllieInputType, disabled: boolean) => {
  if (disabled) return `${colorByType[allieType]}-${backgroundColor[allieType]}`;
  if (allieType === 'primary') return 'white';

  return `${colorByType[allieType]}-${backgroundColor[allieType]}`;
};

const getColor = (state: InternalButtonState, disabled: boolean) => {
  if (disabled) return `neutral-${textColor.disabled}`;

  return `neutral-${textColor[state]}`;
};

const borderByType = {
  primary: 30,
  secondary: 30,
  warning: 50,
  error: 50,
};

const getBorder = (allieType: AllieInputType, border: boolean) =>
  allieType === 'secondary' || border
    ? `${colorByType[allieType]}-${borderByType[allieType]}`
    : 'transparent';

const getOutline = (allieType: AllieInputType) => `${colorByType[allieType]}-50`;

const verticalPadding = {
  normal: 18,
  medium: 16,
  small: 8,
};

interface AllieTextAreaParams {
  allieType: AllieInputType;
  disabled: boolean;
  border: boolean;
  shadow: boolean;
  corner: AllieButtonCorner;
  allieSize: AllieTextAreaSize;
  onkeypress: (event: any) => void;
}

export const StyledTextArea: ComponentType<AllieTextAreaParams> = styled(
  IonTextarea
)<AllieTextAreaParams>`
  --background: var(
    --allie-color-${(props: AllieTextAreaParams) => getBackground(props.allieType, props.disabled)}
  );
  --background-activated: var(
    --allie-color-${(props: AllieTextAreaParams) => getBackground(props.allieType, props.disabled)}
  );
  --background-activated-opacity: 1;
  --background-hover: var(
    --allie-color-${(props: AllieTextAreaParams) => getBackground(props.allieType, props.disabled)}
  );
  --background-hover-opacity: 1;
  --background-focused: var(
    --allie-color-${(props: AllieTextAreaParams) => getBackground(props.allieType, props.disabled)}
  );
  --background-focused-opacity: 1;
  --color: var(
    --allie-color-${(props: AllieTextAreaParams) => getColor('initial', props.disabled)}
  );
  && {
    --color-activated: var(
      --allie-color-${(props: AllieTextAreaParams) => getColor('active', props.disabled)}
    );
  }
  && {
    --color-hover: var(
      --allie-color-${(props: AllieTextAreaParams) => getColor('hover', props.disabled)}
    );
  }
  && {
    --color-focused: var(
      --allie-color-${(props: AllieTextAreaParams) => getColor('focus', props.disabled)}
    );
  }
  border-color: var(
    --allie-color-${(props: AllieTextAreaParams) => getBorder(props.allieType, props.border)}
  );
  border-width: 1px;
  border-style: solid;
  border-radius: ${(props: AllieTextAreaParams) => (props.corner === 'rounded' ? '25' : '8')}px;
  && {
    &:focus {
      outline: solid 3px
        var(--allie-color-${(props: AllieTextAreaParams) => getOutline(props.allieType)});
      box-shadow:
        0px 0px 0px 4px #f4ebff,
        0px 1px 2px 0px rgba(16, 24, 40, 0.05);
    }
  }
  box-shadow: ${(props: AllieTextAreaParams) =>
    props.shadow ? '0px 3px 10px rgba(0, 0, 0, 0.18)' : 'none'};
  text-transform: none;
  &&& {
    --padding-top: ${(props: AllieTextAreaParams) => verticalPadding[props.allieSize]}px;
  }
  &&& {
    --padding-bottom: ${(props: AllieTextAreaParams) => verticalPadding[props.allieSize]}px;
  }
  &&& {
    --padding-start: 14px;
  }
  && {
    --padding-end: 14px;
  }
  --placeholder-color: var(--allie-color-neutral-50);
  --placeholder-opacity: 1;
`;

export const StyledSpan = styled('span')<{
  allieSize?: AllieTextAreaSize;
  suffixAlign?: 'top' | 'bottom';
}>`
  position: absolute;
  right: 14px;
  ${(props) =>
    props.suffixAlign === 'top' && `top: ${verticalPadding[props.allieSize || 'normal']}px`};
  ${(props) =>
    props.suffixAlign === 'bottom' && `bottom: ${verticalPadding[props.allieSize || 'normal']}px`};
  z-index: 2;
`;

export const TextAreaWrapper = styled.div`
  &.input-with-suffix {
    position: relative;
  }
`;
